import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../styles/Authors.css';

const GuideForAuthors = () => {
  const [activeSection, setActiveSection] = useState(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const winScroll = document.documentElement.scrollTop;
      const height = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrolled = (winScroll / height) * 100;
      setScrollProgress(scrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const sections = [
    { id: 'submission', title: 'Submission', icon: 'fas fa-paper-plane' },
    { id: 'types', title: 'Manuscript Types', icon: 'fas fa-file-alt' },
    { id: 'structure', title: 'Structure', icon: 'fas fa-sitemap' },
    { id: 'formatting', title: 'Formatting', icon: 'fas fa-align-left' },
    { id: 'references', title: 'References', icon: 'fas fa-quote-right' },
    { id: 'ethics', title: 'Ethics', icon: 'fas fa-balance-scale' },
    { id: 'fees', title: 'Fees', icon: 'fas fa-credit-card' },
    { id: 'templates', title: 'Templates', icon: 'fas fa-download' },
  ];

  return (
    <>
      {/* Progress Bar */}
      <div className="position-fixed top-0 start-0 w-100" style={{ zIndex: 1000 }}>
        <div
          className="bg-gradient"
          style={{
            height: '4px',
            width: `${scrollProgress}%`,
            background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
            transition: 'width 0.3s ease'
          }}
        />
      </div>

      {/* Hero Section */}
      <div className="hero-section">
        <div className="container">
          <div className="row">
            <div className="col-lg-10 mx-auto text-center">
              <h1 className="hero-title">
                <i className="fas fa-pen-fancy me-3"></i>
                Guide for Authors
              </h1>
              <p className="hero-subtitle">
                Everything you need to know to prepare and submit your manuscript to FUWJALS
              </p>
              <div className="mt-4">
                <Link to="/submit-paper" className="btn btn-light btn-lg me-3" style={{ borderRadius: '50px', padding: '0.75rem 2rem' }}>
                  <i className="fas fa-upload me-2"></i>Submit Manuscript
                </Link>
                {/* <Link to="/manuscript-template" className="btn btn-outline-light btn-lg" style={{ borderRadius: '50px', padding: '0.75rem 2rem' }}>
                  <i className="fas fa-download me-2"></i>Get Templates
                </Link> */}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Navigation */}
      <div className="container py-4">
        <div className="row mb-4">
          <div className="col-lg-10 mx-auto">
            <div className="content-card">
              <h3 className="mb-3"><i className="fas fa-compass me-2 text-primary"></i>Quick Navigation</h3>
              <div className="row">
                {sections.map((section) => (
                  <div key={section.id} className="col-md-3 col-sm-6 mb-3">
                    <button
                      className={`btn btn-outline-primary w-100 ${activeSection === section.id ? 'active' : ''}`}
                      onClick={() => {
                        setActiveSection(section.id);
                        document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth' });
                      }}
                      style={{
                        borderRadius: '10px',
                        padding: '0.75rem',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <i className={`${section.icon} me-2`}></i>
                      <small>{section.title}</small>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="row mb-5">
          <div className="col-lg-10 mx-auto">

            {/* Welcome Section */}
            <div className="content-card" style={{ animationDelay: '0.1s' }}>
              <div className="content-card-header">
                <h2 className="content-card-title">
                  <i className="fas fa-hand-paper me-3 text-primary"></i>
                  Welcome to FUWJALS
                </h2>
              </div>
              <p className="lead">
                Thank you for considering FUWJALS for your research publication. Please read these
                guidelines carefully before submitting your manuscript to ensure a smooth review process.
              </p>
              <div className="alert alert-info" style={{ borderRadius: '10px', border: 'none', boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)' }}>
                <i className="fas fa-lightbulb me-2"></i>
                <strong>Pro Tip:</strong> Download our manuscript templates before you begin writing to ensure
                proper formatting from the start.
              </div>
            </div>

            {/* Submission Process */}
            <div id="submission" className="content-card" style={{ animationDelay: '0.2s' }}>
              <div className="content-card-header">
                <h2 className="content-card-title">
                  <i className="fas fa-paper-plane me-3 text-success"></i>
                  Submission Process
                </h2>
              </div>
              <div className="row">
                <div className="col-lg-12">
                  <div className="timeline-container">
                    <div className="timeline-step">
                      <div className="timeline-badge bg-primary">1</div>
                      <div className="timeline-content">
                        <h4>Create an Account</h4>
                        <p>Register on our submission system with your institutional email address.</p>
                      </div>
                    </div>
                    <div className="timeline-step">
                      <div className="timeline-badge bg-info">2</div>
                      <div className="timeline-content">
                        <h4>Complete Manuscript Information</h4>
                        <p>Fill in all required fields including title, abstract, keywords, and author details.</p>
                      </div>
                    </div>
                    <div className="timeline-step">
                      <div className="timeline-badge bg-warning">3</div>
                      <div className="timeline-content">
                        <h4>Upload Your Files</h4>
                        <p>Upload your manuscript (Word format), cover letter, and supplementary materials.</p>
                      </div>
                    </div>
                    <div className="timeline-step">
                      <div className="timeline-badge bg-success">4</div>
                      <div className="timeline-content">
                        <h4>Review and Submit</h4>
                        <p>Review your submission details and confirm to complete the process.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row mt-4">
                <div className="col-md-6">
                  <div className="review-card">
                    <h4 className="review-card-title">
                      <i className="fas fa-file-upload me-2 text-primary"></i>
                      Required Files
                    </h4>
                    <ul className="review-card-content">
                      <li><i className="fas fa-check text-success me-2"></i>Main manuscript (Word format)</li>
                      <li><i className="fas fa-check text-success me-2"></i>Cover letter</li>
                      <li><i className="fas fa-check text-success me-2"></i>Figures and tables (separate files)</li>
                      <li><i className="fas fa-check text-success me-2"></i>Supplementary materials (if any)</li>
                    </ul>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="review-card">
                    <h4 className="review-card-title">
                      <i className="fas fa-exclamation-triangle me-2 text-warning"></i>
                      Before Submitting
                    </h4>
                    <ul className="review-card-content">
                      <li><i className="fas fa-check text-success me-2"></i>Check manuscript formatting</li>
                      <li><i className="fas fa-check text-success me-2"></i>Verify all author information</li>
                      <li><i className="fas fa-check text-success me-2"></i>Ensure figures are high resolution</li>
                      <li><i className="fas fa-check text-success me-2"></i>Complete ethics declarations</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Manuscript Types */}
            <div id="types" className="content-card" style={{ animationDelay: '0.3s' }}>
              <div className="content-card-header">
                <h2 className="content-card-title">
                  <i className="fas fa-file-alt me-3 text-info"></i>
                  Manuscript Types
                </h2>
              </div>
              <div className="row">
                <div className="col-md-4 mb-4">
                  <div className="template-card">
                    <div className="template-card-header" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                      <h5 className="mb-0">
                        <i className="fas fa-flask me-2"></i>
                        Original Research
                      </h5>
                    </div>
                    <div className="template-card-body">
                      <ul className="template-features">
                        <li><i className="fas fa-check"></i>Word limit: 6,000 words</li>
                        <li><i className="fas fa-check"></i>Abstract: 250 words</li>
                        <li><i className="fas fa-check"></i>References: Up to 50</li>
                        <li><i className="fas fa-check"></i>Figures/Tables: Up to 8</li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="col-md-4 mb-4">
                  <div className="template-card">
                    <div className="template-card-header" style={{ background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)' }}>
                      <h5 className="mb-0">
                        <i className="fas fa-book-open me-2"></i>
                        Review Articles
                      </h5>
                    </div>
                    <div className="template-card-body">
                      <ul className="template-features">
                        <li><i className="fas fa-check"></i>Word limit: 8,000 words</li>
                        <li><i className="fas fa-check"></i>Abstract: 300 words</li>
                        <li><i className="fas fa-check"></i>References: Up to 100</li>
                        <li><i className="fas fa-check"></i>Figures/Tables: Up to 10</li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="col-md-4 mb-4">
                  <div className="template-card">
                    <div className="template-card-header" style={{ background: 'linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)' }}>
                      <h5 className="mb-0">
                        <i className="fas fa-bolt me-2"></i>
                        Short Communications
                      </h5>
                    </div>
                    <div className="template-card-body">
                      <ul className="template-features">
                        <li><i className="fas fa-check"></i>Word limit: 2,000 words</li>
                        <li><i className="fas fa-check"></i>Abstract: 150 words</li>
                        <li><i className="fas fa-check"></i>References: Up to 20</li>
                        <li><i className="fas fa-check"></i>Figures/Tables: Up to 3</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Manuscript Structure */}
            <div id="structure" className="content-card" style={{ animationDelay: '0.4s' }}>
              <div className="content-card-header">
                <h2 className="content-card-title">
                  <i className="fas fa-sitemap me-3 text-warning"></i>
                  Manuscript Structure
                </h2>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <div className="review-card">
                    <h4 className="review-card-title">
                      <i className="fas fa-id-card me-2"></i>
                      Title Page
                    </h4>
                    <ul className="review-card-content">
                      <li>Full title (concise and informative)</li>
                      <li>Short running title (≤50 characters)</li>
                      <li>Author names and affiliations</li>
                      <li>Corresponding author details</li>
                      <li>Word count and declaration statements</li>
                    </ul>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="review-card">
                    <h4 className="review-card-title">
                      <i className="fas fa-align-justify me-2"></i>
                      Abstract
                    </h4>
                    <ul className="review-card-content">
                      <li>Structured: Background, Methods, Results, Conclusions</li>
                      <li>3-6 keywords below the abstract</li>
                      <li>No references or abbreviations</li>
                      <li>Stand-alone summary of your work</li>
                    </ul>
                  </div>
                </div>
              </div>

              <h4 className="mt-4 mb-3"><i className="fas fa-list-ol me-2 text-primary"></i>Main Text Structure</h4>
              <div className="timeline-container">
                <div className="timeline-step">
                  <div className="timeline-badge bg-primary">1</div>
                  <div className="timeline-content">
                    <h5>Introduction</h5>
                    <p>Background context and research objectives</p>
                  </div>
                </div>
                <div className="timeline-step">
                  <div className="timeline-badge bg-info">2</div>
                  <div className="timeline-content">
                    <h5>Materials and Methods</h5>
                    <p>Detailed methodology enabling reproducibility</p>
                  </div>
                </div>
                <div className="timeline-step">
                  <div className="timeline-badge bg-success">3</div>
                  <div className="timeline-content">
                    <h5>Results</h5>
                    <p>Findings without interpretation</p>
                  </div>
                </div>
                <div className="timeline-step">
                  <div className="timeline-badge bg-warning">4</div>
                  <div className="timeline-content">
                    <h5>Discussion</h5>
                    <p>Interpretation and implications of results</p>
                  </div>
                </div>
                <div className="timeline-step">
                  <div className="timeline-badge bg-danger">5</div>
                  <div className="timeline-content">
                    <h5>Conclusion</h5>
                    <p>Brief summary of key findings</p>
                  </div>
                </div>
                <div className="timeline-step">
                  <div className="timeline-badge bg-secondary">6</div>
                  <div className="timeline-content">
                    <h5>Acknowledgments & References</h5>
                    <p>Funding, contributors, and complete citations</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Formatting Guidelines */}
            <div id="formatting" className="content-card" style={{ animationDelay: '0.5s' }}>
              <div className="content-card-header">
                <h2 className="content-card-title">
                  <i className="fas fa-align-left me-3 text-success"></i>
                  Formatting Guidelines
                </h2>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <div className="review-card">
                    <h4 className="review-card-title">
                      <i className="fas fa-font me-2"></i>
                      General Format
                    </h4>
                    <ul className="review-card-content">
                      <li><strong>Font:</strong> Times New Roman, 12-point</li>
                      <li><strong>Spacing:</strong> Double-line spacing</li>
                      <li><strong>Margins:</strong> 1 inch on all sides</li>
                      <li><strong>Page numbers:</strong> Bottom center</li>
                      <li><strong>File format:</strong> Microsoft Word (.docx)</li>
                    </ul>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="review-card">
                    <h4 className="review-card-title">
                      <i className="fas fa-image me-2"></i>
                      Figures and Tables
                    </h4>
                    <ul className="review-card-content">
                      <li><strong>Resolution:</strong> Minimum 300 dpi</li>
                      <li><strong>Formats:</strong> JPEG, PNG, TIFF for figures</li>
                      <li><strong>Tables:</strong> Editable Word format</li>
                      <li><strong>Captions:</strong> Clear and descriptive</li>
                      <li><strong>Citation:</strong> All must be cited in text</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Reference Style */}
            <div id="references" className="content-card" style={{ animationDelay: '0.6s' }}>
              <div className="content-card-header">
                <h2 className="content-card-title">
                  <i className="fas fa-quote-right me-3 text-info"></i>
                  Reference Style (APA)
                </h2>
              </div>
              <p>FUWJALS uses the APA (American Psychological Association) style for references.</p>

              <div className="row mt-4">
                <div className="col-md-12">
                  <div className="alert mb-3" style={{ background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)', borderRadius: '10px', border: 'none' }}>
                    <h5><i className="fas fa-newspaper me-2 text-primary"></i>Journal Article</h5>
                    <code style={{ display: 'block', background: 'rgba(0,0,0,0.05)', padding: '1rem', borderRadius: '8px', fontSize: '0.9rem' }}>
                      Smith, J. A., & Brown, B. C. (2023). Sustainable agriculture practices in West Africa.
                      <em>Journal of Agricultural Sciences, 15</em>(3), 45-62.
                    </code>
                  </div>

                  <div className="alert mb-3" style={{ background: 'linear-gradient(135deg, rgba(40, 167, 69, 0.1) 0%, rgba(32, 201, 151, 0.1) 100%)', borderRadius: '10px', border: 'none' }}>
                    <h5><i className="fas fa-book me-2 text-success"></i>Book</h5>
                    <code style={{ display: 'block', background: 'rgba(0,0,0,0.05)', padding: '1rem', borderRadius: '8px', fontSize: '0.9rem' }}>
                      Johnson, M. K. (2022). <em>Modern crop production techniques</em> (3rd ed.). Academic Press.
                    </code>
                  </div>

                  <div className="alert mb-3" style={{ background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(253, 126, 20, 0.1) 100%)', borderRadius: '10px', border: 'none' }}>
                    <h5><i className="fas fa-bookmark me-2 text-warning"></i>Book Chapter</h5>
                    <code style={{ display: 'block', background: 'rgba(0,0,0,0.05)', padding: '1rem', borderRadius: '8px', fontSize: '0.9rem' }}>
                      Davis, R. L. (2021). Soil management strategies. In P. Wilson (Ed.),
                      <em>Agricultural sustainability</em> (pp. 123-145). University Press.
                    </code>
                  </div>
                </div>
              </div>
            </div>

            {/* Ethical Requirements */}
            <div id="ethics" className="content-card" style={{ animationDelay: '0.7s' }}>
              <div className="content-card-header">
                <h2 className="content-card-title">
                  <i className="fas fa-balance-scale me-3 text-danger"></i>
                  Ethical Requirements
                </h2>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <div className="review-card" style={{ borderLeftColor: '#dc3545' }}>
                    <h4 className="review-card-title">
                      <i className="fas fa-clipboard-check me-2 text-danger"></i>
                      Ethics Approval
                    </h4>
                    <p className="review-card-content">
                      Required for all human and animal research. Include ethics committee name and approval number.
                    </p>
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="review-card" style={{ borderLeftColor: '#28a745' }}>
                    <h4 className="review-card-title">
                      <i className="fas fa-user-shield me-2 text-success"></i>
                      Informed Consent
                    </h4>
                    <p className="review-card-content">
                      Must be obtained from all human subjects. Include statement in manuscript.
                    </p>
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="review-card" style={{ borderLeftColor: '#ffc107' }}>
                    <h4 className="review-card-title">
                      <i className="fas fa-exclamation-circle me-2 text-warning"></i>
                      Conflict of Interest
                    </h4>
                    <p className="review-card-content">
                      All potential conflicts must be declared. If none, state "The authors declare no conflicts."
                    </p>
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="review-card" style={{ borderLeftColor: '#17a2b8' }}>
                    <h4 className="review-card-title">
                      <i className="fas fa-file-signature me-2 text-info"></i>
                      Originality
                    </h4>
                    <p className="review-card-content">
                      Manuscript must be original and not under consideration elsewhere. Plagiarism will result in rejection.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Review Timeline */}
            <div className="content-card" style={{ animationDelay: '0.8s' }}>
              <div className="content-card-header">
                <h2 className="content-card-title">
                  <i className="fas fa-clock me-3 text-primary"></i>
                  Review Timeline
                </h2>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <div className="review-card">
                    <h4 className="review-card-title">
                      <i className="fas fa-hourglass-half me-2"></i>
                      Expected Timeline
                    </h4>
                    <ul className="review-card-content">
                      <li><strong>Initial review:</strong> 2-3 weeks</li>
                      <li><strong>Peer review:</strong> 4-6 weeks</li>
                      <li><strong>Revision time:</strong> 4 weeks</li>
                      <li><strong>Final decision:</strong> 1-2 weeks</li>
                    </ul>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="review-card">
                    <h4 className="review-card-title">
                      <i className="fas fa-gavel me-2"></i>
                      Decision Categories
                    </h4>
                    <ul className="review-card-content">
                      <li><span className="badge bg-success me-2">Accept</span>No revision needed</li>
                      <li><span className="badge bg-primary me-2">Minor</span>Small changes required</li>
                      <li><span className="badge bg-warning text-dark me-2">Major</span>Significant revision</li>
                      <li><span className="badge bg-danger me-2">Reject</span>Not suitable</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Publication Charges */}
            <div id="fees" className="content-card" style={{ animationDelay: '0.9s' }}>
              <div className="content-card-header">
                <h2 className="content-card-title">
                  <i className="fas fa-credit-card me-3 text-success"></i>
                  Publication Charges
                </h2>
              </div>
              <p>
                FUWJALS operates as an open-access journal. A publication fee is charged upon acceptance
                to cover peer review, editing, and online hosting costs.
              </p>
              <div className="row mt-4">
                <div className="col-md-3 col-6 mb-3">
                  <div className="text-center p-3" style={{ background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)', borderRadius: '12px' }}>
                    <i className="fas fa-tag fa-2x text-primary mb-2"></i>
                    <h4 className="mb-1">$200</h4>
                    <small className="text-muted">Standard Fee</small>
                  </div>
                </div>
                <div className="col-md-3 col-6 mb-3">
                  <div className="text-center p-3" style={{ background: 'linear-gradient(135deg, rgba(40, 167, 69, 0.1) 0%, rgba(32, 201, 151, 0.1) 100%)', borderRadius: '12px' }}>
                    <i className="fas fa-globe-africa fa-2x text-success mb-2"></i>
                    <h4 className="mb-1">50% Off</h4>
                    <small className="text-muted">Low-Income Countries</small>
                  </div>
                </div>
                <div className="col-md-3 col-6 mb-3">
                  <div className="text-center p-3" style={{ background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(253, 126, 20, 0.1) 100%)', borderRadius: '12px' }}>
                    <i className="fas fa-user-graduate fa-2x text-warning mb-2"></i>
                    <h4 className="mb-1">25% Off</h4>
                    <small className="text-muted">Student Authors</small>
                  </div>
                </div>
                <div className="col-md-3 col-6 mb-3">
                  <div className="text-center p-3" style={{ background: 'linear-gradient(135deg, rgba(23, 162, 184, 0.1) 0%, rgba(13, 202, 240, 0.1) 100%)', borderRadius: '12px' }}>
                    <i className="fas fa-calendar-check fa-2x text-info mb-2"></i>
                    <h4 className="mb-1">Upon Acceptance</h4>
                    <small className="text-muted">Payment Due</small>
                  </div>
                </div>
              </div>
              <div className="text-center mt-3">
                <Link to="/publication-fee" className="btn btn-outline-primary" style={{ borderRadius: '50px', padding: '0.5rem 2rem' }}>
                  <i className="fas fa-info-circle me-2"></i>View Fee Details
                </Link>
              </div>
            </div>

            {/* Templates and Resources */}
            <div id="templates" className="content-card" style={{ animationDelay: '1.0s' }}>
              <div className="content-card-header">
                <h2 className="content-card-title">
                  <i className="fas fa-download me-3 text-primary"></i>
                  Templates & Resources
                </h2>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <h4><i className="fas fa-file-download me-2 text-success"></i>Download Templates</h4>
                  {/* <ul className="list-group list-group-flush">
                    <li className="list-group-item d-flex justify-content-between align-items-center" style={{ border: 'none', padding: '0.75rem 0' }}>
                      <span><i className="fas fa-file-word text-primary me-2"></i>Manuscript Template</span>
                      <Link to="/manuscript-template" className="btn btn-sm btn-outline-primary" style={{ borderRadius: '20px' }}>
                        Download
                      </Link>
                    </li>
                    <li className="list-group-item d-flex justify-content-between align-items-center" style={{ border: 'none', padding: '0.75rem 0' }}>
                      <span><i className="fas fa-envelope text-info me-2"></i>Cover Letter Template</span>
                      <Link to="/manuscript-template" className="btn btn-sm btn-outline-primary" style={{ borderRadius: '20px' }}>
                        Download
                      </Link>
                    </li>
                    <li className="list-group-item d-flex justify-content-between align-items-center" style={{ border: 'none', padding: '0.75rem 0' }}>
                      <span><i className="fas fa-reply text-warning me-2"></i>Revision Response Template</span>
                      <Link to="/manuscript-template" className="btn btn-sm btn-outline-primary" style={{ borderRadius: '20px' }}>
                        Download
                      </Link>
                    </li>
                  </ul> */}
                  <p className="text-muted"><i className="fas fa-info-circle me-2"></i>Templates are currently unavailable. Please contact the editorial office for formatting guidelines.</p>
                </div>
                <div className="col-md-6">
                  <h4><i className="fas fa-external-link-alt me-2 text-info"></i>Helpful Resources</h4>
                  <ul className="list-group list-group-flush">
                    <li className="list-group-item d-flex justify-content-between align-items-center" style={{ border: 'none', padding: '0.75rem 0' }}>
                      <span><i className="fas fa-book-reader text-success me-2"></i>Mendeley Reference Manager</span>
                      <a href="https://www.mendeley.com" target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-secondary" style={{ borderRadius: '20px' }}>
                        Visit
                      </a>
                    </li>
                    <li className="list-group-item d-flex justify-content-between align-items-center" style={{ border: 'none', padding: '0.75rem 0' }}>
                      <span><i className="fas fa-folder-open text-danger me-2"></i>Zotero Reference Manager</span>
                      <a href="https://www.zotero.org" target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-secondary" style={{ borderRadius: '20px' }}>
                        Visit
                      </a>
                    </li>
                    <li className="list-group-item d-flex justify-content-between align-items-center" style={{ border: 'none', padding: '0.75rem 0' }}>
                      <span><i className="fas fa-spell-check text-primary me-2"></i>Grammarly</span>
                      <a href="https://www.grammarly.com" target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-secondary" style={{ borderRadius: '20px' }}>
                        Visit
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Contact Section */}
            <div className="content-card" style={{
              animationDelay: '1.1s',
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)'
            }}>
              <div className="content-card-header">
                <h2 className="content-card-title">
                  <i className="fas fa-headset me-3 text-primary"></i>
                  Need Help?
                </h2>
              </div>
              <p>For questions about manuscript preparation or submission, please contact us:</p>
              <div className="row">
                <div className="col-md-6">
                  <div className="d-flex align-items-center mb-3">
                    <div className="me-3" style={{ width: '50px', height: '50px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <i className="fas fa-envelope text-white"></i>
                    </div>
                    <div>
                      <strong>Editorial Office</strong><br />
                      <a href="mailto:submissions@fuwukari.edu.ng">submissions@fuwukari.edu.ng</a>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex align-items-center mb-3">
                    <div className="me-3" style={{ width: '50px', height: '50px', background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <i className="fas fa-tools text-white"></i>
                    </div>
                    <div>
                      <strong>Technical Support</strong><br />
                      <a href="mailto:support@fuwukari.edu.ng">support@fuwukari.edu.ng</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Call to Action */}
            <div className="text-center mt-5">
              <Link to="/submit-paper" className="btn btn-primary btn-lg me-3" style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                borderRadius: '50px',
                padding: '0.75rem 2rem',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
              }}>
                <i className="fas fa-upload me-2"></i>
                Submit Your Manuscript
              </Link>
              {/* <Link to="/manuscript-template" className="btn btn-outline-primary btn-lg" style={{
                borderRadius: '50px',
                padding: '0.75rem 2rem',
                borderWidth: '2px'
              }}>
                <i className="fas fa-download me-2"></i>
                Download Templates
              </Link> */}
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default GuideForAuthors;
